import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useReport, type ReportTargetType } from "@/hooks/useReport";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ─── Motifs par type de cible ─────────────────────────────────────────────────

const REASONS: Record<ReportTargetType, { value: string; label: string }[]> = {
  user: [
    { value: "fake_profile", label: "Profil faux ou usurpation d'identité" },
    { value: "scam", label: "Arnaque ou tentative d'escroquerie" },
    { value: "inappropriate_behavior", label: "Comportement inapproprié ou agressif" },
    { value: "spam", label: "Spam ou démarchage abusif" },
    { value: "other", label: "Autre raison" },
  ],
  ad: [
    { value: "fake_ad", label: "Annonce fausse ou trompeuse" },
    { value: "scam", label: "Arnaque ou tentative d'escroquerie" },
    { value: "inappropriate_content", label: "Contenu inapproprié ou offensant" },
    { value: "wrong_category", label: "Mauvaise catégorie ou hors-sujet" },
    { value: "duplicate", label: "Annonce dupliquée" },
    { value: "other", label: "Autre raison" },
  ],
};

const TARGET_LABELS: Record<ReportTargetType, string> = {
  user: "cet utilisateur",
  ad: "cette annonce",
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: string;
  targetName?: string;
}

// ─── Composant ────────────────────────────────────────────────────────────────

export function ReportModal({ open, onClose, targetType, targetId, targetName }: ReportModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedReason, setSelectedReason] = useState("");
  const [details, setDetails] = useState("");
  const reportMutation = useReport();

  const reasons = REASONS[targetType];

  const handleClose = () => {
    setSelectedReason("");
    setDetails("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("Vous devez être connecté pour signaler");
      navigate("/connexion");
      return;
    }

    // UX-layer guard: prevent self-reporting before the request is even sent.
    if (targetType === "user" && targetId === user.id) {
      toast.error("Vous ne pouvez pas vous signaler vous-même.");
      return;
    }

    if (!selectedReason) return;

    // reportMutation.isPending already prevents double-submission (button is disabled),
    // but guard here too to be safe if called programmatically.
    if (reportMutation.isPending) return;

    await reportMutation.mutateAsync(
      { targetType, targetId, reason: selectedReason, details },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-destructive" />
            Signaler {targetName ? `"${targetName}"` : TARGET_LABELS[targetType]}
          </DialogTitle>
          <DialogDescription>
            Votre signalement sera examiné par notre équipe de modération. Merci de choisir le motif le plus pertinent.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Motifs */}
          <div className="space-y-2">
            {reasons.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSelectedReason(value)}
                className={`w-full rounded-lg border px-4 py-2.5 text-left text-sm transition-all ${
                  selectedReason === value
                    ? "border-destructive bg-destructive/5 ring-1 ring-destructive text-destructive font-medium"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Détails optionnels */}
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Détails supplémentaires <span className="text-gray-400">(optionnel)</span>
            </p>
            <Textarea
              placeholder="Décrivez le problème en quelques mots..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="resize-none text-sm min-h-[80px]"
              maxLength={500}
            />
            {details.length > 400 && (
              <p className="text-xs text-muted-foreground text-right">{details.length}/500</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={reportMutation.isPending}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!selectedReason || reportMutation.isPending}
          >
            {reportMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Flag className="mr-2 h-4 w-4" />
            )}
            Envoyer le signalement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
